import { useContext, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import LoggedInUserContext from '../../contexts/LoggedInUserContext';
import NotificationsContext from '../../contexts/NotificationsContext';
import useBottomSheetDrag from '../../hooks/useBottomSheetDrag';
import { updateUserProfileData } from '../../services/userService';
import { NOTIFICATIONS_BADGE_MAX_COUNT, NOTIFICATIONS_TITLE } from '../../constants/notifications';

import BottomSheetHandle from '../BottomSheetHandle/BottomSheetHandle';

import './AccountMenu.css';

const AccountMenu = ({ isOpen, onClose, logoutHandler, onChangePasswordClick, onNotificationsClick, className = '', isBottomSheet = false }) => {
	const { loggedInUser, jwtToken, updateLoggedInUser } = useContext(LoggedInUserContext);
	const { unreadCount } = useContext(NotificationsContext);
	const menuRef = useRef(null);
	const bottomSheetDragProps = useBottomSheetDrag(onClose);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const closeOnOutsideClick = (event) => {
			if (menuRef.current && !menuRef.current.contains(event.target)) {
				onClose();
			}
		};

		const closeOnEscape = (event) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};

		document.addEventListener('mousedown', closeOnOutsideClick);
		document.addEventListener('keydown', closeOnEscape);

		return () => {
			document.removeEventListener('mousedown', closeOnOutsideClick);
			document.removeEventListener('keydown', closeOnEscape);
		};
	}, [isOpen, onClose]);

	if (!isOpen) {
		return null;
	}

	const isDark = loggedInUser?.theme === 'dark';

	const toggleTheme = async () => {
		const updatedUser = await updateUserProfileData({
			userId: loggedInUser._id,
			jwtToken,
			updatedProfileData: { theme: isDark ? 'light' : 'dark' },
		});

		updateLoggedInUser(updatedUser);
	};

	const changePasswordClickHandler = () => {
		onClose();
		onChangePasswordClick();
	};

	const notificationsClickHandler = () => {
		onClose();
		onNotificationsClick();
	};

	const unreadBadgeLabel = unreadCount > NOTIFICATIONS_BADGE_MAX_COUNT ? `${NOTIFICATIONS_BADGE_MAX_COUNT}+` : unreadCount;

	return (
		<div className={`account-menu ${className}`.trim()} ref={menuRef} {...(isBottomSheet ? bottomSheetDragProps : {})}>
			{isBottomSheet ? <BottomSheetHandle /> : null}

			<Link to={`/user/${loggedInUser._id}`} className='account-menu-row' onClick={onClose}>
				<span>Profile</span>
			</Link>

			<button type='button' className='account-menu-row' onClick={notificationsClickHandler}>
				<span>{NOTIFICATIONS_TITLE}</span>

				{unreadCount > 0 ? <span className='account-menu-badge'>{unreadBadgeLabel}</span> : null}
			</button>

			<button type='button' className='account-menu-row' onClick={toggleTheme} aria-pressed={isDark}>
				<span>Dark mode</span>

				<span className={`account-menu-switch ${isDark ? 'account-menu-switch-on' : ''}`} />
			</button>

			<button type='button' className='account-menu-row' onClick={changePasswordClickHandler}>
				<span>Change password</span>
			</button>

			<button type='button' className='account-menu-row' onClick={logoutHandler}>
				<span>Log out</span>
			</button>
		</div>
	);
};

export default AccountMenu;
